import React from 'react';
import { format } from 'date-fns';

const LoanStatusTimeline = ({ loan }) => {
  if (!loan) return null;

  const getStatusIcon = (status) => {
    const iconClasses = "w-6 h-6";
    
    switch (status) {
      case 'PENDING':
        return (
          <svg className={`${iconClasses} text-yellow-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'APPROVED':
        return (
          <svg className={`${iconClasses} text-green-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'DISAPPROVED':
        return (
          <svg className={`${iconClasses} text-red-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'ACTIVE':
        return (
          <svg className={`${iconClasses} text-blue-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
          </svg>
        );
      default:
        return (
          <svg className={`${iconClasses} text-gray-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'APPROVED':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'DISAPPROVED':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'ACTIVE':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'PENDING':
        return 'Pending Approval';
      case 'APPROVED':
        return 'Approved';
      case 'DISAPPROVED':
        return 'Disapproved';
      case 'ACTIVE':
        return 'Disbursed';
      default:
        return status;
    }
  };

  const buildTimeline = () => {
    const timeline = [];

    // Application submitted
    timeline.push({
      id: 'application',
      status: 'PENDING',
      title: 'Loan Application Submitted',
      description: `Loan application #${loan.loan_no} was submitted`,
      timestamp: loan.created_at,
      user: loan.applied_by,
      notes: loan.reason,
      isCurrent: loan.status === 'PENDING'
    });

    // If approved, add approval event
    if (['APPROVED', 'ACTIVE', 'COMPLETED', 'PAID'].includes(loan.status)) {
      timeline.push({
        id: 'approval',
        status: 'APPROVED',
        title: 'Loan Approved',
        description: 'Loan application has been approved',
        timestamp: loan.updated_at,
        user: loan.approved_by,
        notes: loan.approval_notes,
        isCurrent: loan.status === 'APPROVED'
      });
    }

    // If disapproved, add disapproval event
    if (loan.status === 'DISAPPROVED') {
      timeline.push({
        id: 'disapproval',
        status: 'DISAPPROVED',
        title: 'Loan Disapproved',
        description: 'Loan application has been disapproved',
        timestamp: loan.updated_at,
        user: loan.approved_by,
        notes: loan.approval_notes,
        isCurrent: true
      });
    }

    // If disbursed, add disbursement event
    if (['ACTIVE', 'COMPLETED', 'PAID'].includes(loan.status)) {
      timeline.push({
        id: 'disbursement',
        status: 'ACTIVE',
        title: 'Loan Disbursed',
        description: 'Loan has been disbursed to the member',
        timestamp: loan.disbursed_at,
        user: loan.disbursed_by,
        notes: loan.disbursement_notes,
        isCurrent: loan.status === 'ACTIVE'
      });
    }

    return timeline.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  };

  const timeline = buildTimeline();

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Loan Approval Timeline</h3>
        <p className="text-sm text-gray-600">
          Complete history of the loan approval workflow
        </p>
      </div>

      <div className="flow-root">
        <ul className="-mb-8">
          {timeline.map((event, eventIdx) => (
            <li key={event.id}>
              <div className="relative pb-8">
                {eventIdx !== timeline.length - 1 ? (
                  <span
                    className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                    aria-hidden="true"
                  />
                ) : null}
                <div className="relative flex space-x-3">
                  <div>
                    <span className={`h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white ${
                      event.isCurrent ? 'bg-blue-500' : 'bg-gray-400'
                    }`}>
                      {getStatusIcon(event.status)}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                    <div>
                      <div className="flex items-center space-x-2">
                        <p className="text-sm font-medium text-gray-900">
                          {event.title}
                        </p>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(event.status)}`}>
                          {getStatusText(event.status)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        {event.description}
                      </p>
                      {event.notes && (
                        <div className="mt-2 p-3 bg-gray-50 rounded-md">
                          <p className="text-sm text-gray-700">
                            <span className="font-medium">Notes:</span> {event.notes}
                          </p>
                        </div>
                      )}
                      {event.user && (
                        <div className="mt-2 flex items-center space-x-2">
                          <div className="flex-shrink-0">
                            <div className="h-6 w-6 rounded-full bg-gray-300 flex items-center justify-center">
                              <span className="text-xs font-medium text-gray-700">
                                {event.user.name?.charAt(0)?.toUpperCase() || 'U'}
                              </span>
                            </div>
                          </div>
                          <div className="text-sm text-gray-500">
                            <span className="font-medium">{event.user.name}</span>
                            {event.user.email && (
                              <span className="ml-1">({event.user.email})</span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="text-right text-sm whitespace-nowrap text-gray-500">
                      <time dateTime={event.timestamp}>
                        {format(new Date(event.timestamp), 'MMM dd, yyyy')}
                      </time>
                      <br />
                      <time dateTime={event.timestamp}>
                        {format(new Date(event.timestamp), 'HH:mm')}
                      </time>
                    </div>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Current Status Summary */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-medium text-gray-900">Current Status</h4>
            <p className="text-sm text-gray-500">
              Loan #{loan.loan_no} is currently {getStatusText(loan.status).toLowerCase()}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            {getStatusIcon(loan.status)}
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(loan.status)}`}>
              {getStatusText(loan.status)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoanStatusTimeline; 